import { Box, Button, chakra } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { ThemeColors } from '@theme/foundations/colors';
import ScreenSize from '@view/constants/screen-sizes';

export const StyledDropdown = styled(Box)<{
  hasIcon: boolean;
  labelPosition?: 'left' | 'top' | 'float' | 'default';
}>`
  position: relative;
  ${({ hasIcon, labelPosition }) =>
    labelPosition === 'float' &&
    css`
    .Active {
      transform: translate(${hasIcon ? '48px' : '16px'}, 8px) scale(0.75);
      z-index: 3,
    }
  `};
`;

const Label = chakra('label', {
  shouldForwardProp: (prop) => !['hidden'].includes(prop),
  baseStyle: {
    fontSize: 14,
    lineHeight: '24px',
    fontWeight: 400,
    color: 'black',
    m: '0 4px 4px 0',
  },
});

export const StyledLabel = styled(Label)<{
  hasIcon: boolean;
  hidden: boolean;
  labelPosition?: 'left' | 'top' | 'float' | 'default';
  error?: boolean;
}>`
  ${({ hidden }) =>
    hidden &&
    css`
    clip: rect(1px, 1px, 1px, 1px);
    height: 1px;
    overflow: hidden;
    position: absolute !important;
    width: 1px;
  `};

  ${({ hasIcon, labelPosition }) =>
    labelPosition === 'float' &&
    css`
    color: ${ThemeColors.neutral['600']};
    font-weight: 500;
    position: relative;
    position: absolute;
    transform: translate(${hasIcon ? '48px' : '16px'}, 15px) scale(1);
    transform-origin: top left;
    transition: all 0.2s ease-out;
    z-index: 3;
  `};

  ${({ labelPosition, error }) =>
    labelPosition === 'default' &&
    css`
    position: absolute;
    color: ${error ? ThemeColors.error[600] : ThemeColors.neutral[700]};
    font-weight: 500;
    left: 16px;
    top: 4px;
    z-index: 3;
  `};
`;

const DropdownControlWrap = chakra('div', {
  baseStyle: {
    w: '100%',
  },
});

export const StyledDropdownControlWrap = styled(DropdownControlWrap)<{
  labelPosition?: 'left' | 'top' | 'float' | 'default';
}>`
  flex: ${(props) => (props.labelPosition === 'left' ? 1 : null)};

  &:focus-within button {
    box-shadow: inset 0 0 0 1px ${ThemeColors.black['500']};
  }
`;

const DropdownMenu = chakra('ul', {
  baseStyle: {
    listStyle: 'none',
    p: '8px 16px',
    maxH: '30rem',
    overflow: 'auto',
    w: '100%',
    zIndex: 2,
  },
});

export const StyledDropdownMenu = styled(DropdownMenu)`
`;

export const StyledCloseButton = styled.button`
  display: none;

  &::selection {
      border: none;
  }

  @media screen and (max-width: ${ScreenSize.MOBILE_LARGE}) {
    position: absolute;
    width: 48px;
    height: 48px;
    background-color: #FFFFFF;
    top: 4px;
    right: 0px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    cursor: pointer;
    border: none;
  }
`;

export const StyledDropdownToggle = styled(Button)<{
  hasIcon: boolean;
  bordered?: boolean;
  valueSet?: boolean;
  error?: boolean;
}>`
  font-size: 16px;
  line-height: 24px;
  background-color: ${(props) =>
    props.error ? ThemeColors.error[50] : ThemeColors.white[500]};
  box-shadow: inset 0 0 0 ${(props) => (props.error ? '2px' : '1px')} ${(
  props
) =>
  props.bordered
    ? props.error
      ? ThemeColors.error[600]
      : ThemeColors.neutral[500]
    : 'transparent'};
  color: ${ThemeColors.black['500']};
  cursor: pointer;
  display: ${({ hasIcon }) => (hasIcon ? 'grid' : 'inline-grid')};
  font-weight: 400;
  grid-column-gap: 8px;
  grid-template-columns: ${({ hasIcon }) =>
    hasIcon ? 'max-content calc(100% - 64px) max-content' : 'auto 24px'};
  justify-content: space-between;
  width: 100%;
  border-radius: 8px;
  height: 56px;

  &:active {
    transform: none;
  }

  &:hover {
    background-color: ${ThemeColors.white['500']};
  }

  &.Disabled {
    background-color: ${ThemeColors.neutral['500']};
  }
`;

const ToggleText = chakra('span', {
  baseStyle: {
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 400,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'start',
    whiteSpace: 'nowrap',
  },
});

export const StyledDropdownToggleText = styled(ToggleText)<{
  labelPosition?: 'left' | 'top' | 'float' | 'default';
}>`
  ${({ labelPosition }) =>
    (labelPosition === 'float' || labelPosition === 'default') &&
    css`
    font-size: 14px;
    margin-top: 24px;
    font-weight: 400;
  `};
`;
